import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { type ReactElement } from 'react';

import { ModuleWrapper } from '@/app/components/client/module-wrapper';
import {
  type DatoBackgroundColor,
  type DatoCourse,
  type DatoIconBlock,
} from '@/lib/datocms/types';
import { OptionIcon } from './option-icon';

export type CourseAnswer = {
  answer: {
    backgroundColor: DatoBackgroundColor;
    icon: Pick<Required<DatoIconBlock>, '__typename' | 'color' | 'name'>;
    summaryText: string;
    text: string;
  };
  code: string;
  courses: Array<Pick<DatoCourse, 'code'>>;
};

export type SelectedCourseAnswer = CourseAnswer & {
  questionCode: string;
};

export type IconQuestionData = {
  __typename: 'CourseQuestionRecord';
  answers: CourseAnswer[];
  code: string;
  question: string;
};

type Props = {
  color: string;
  data: IconQuestionData;
  onAnswer?: (answer: CourseAnswer) => void;
} & FlexProps;

export const IconQuestion = (props: Props): ReactElement => {
  const {
    color,
    data: { answers, question },
    onAnswer,
    ...flexProps
  } = props;

  return (
    <ModuleWrapper
      innerProps={{
        align: 'center',
        color,
        direction: 'column',
        gap: 5,
        textAlign: 'center',
        ...flexProps,
      }}
      moduleFormat={{
        background: 'transparent',
        spacingBottom: '24',
        spacingTop: '24',
      }}
    >
      <Text as="h1" maxWidth="612px" textStyle={['3xl', null, '4xl']}>
        {question}
      </Text>

      <Flex direction="column" gap={2} maxWidth="612px" width="full">
        {answers.map((answerData: CourseAnswer) => (
          <Flex
            align="center"
            as="button"
            background={`background.${answerData.answer.backgroundColor.backgroundColor}`}
            borderRadius="base"
            gap={2}
            key={answerData.answer.text}
            onClick={() => onAnswer?.(answerData)}
            padding={2}
          >
            <OptionIcon {...answerData.answer.icon} />
            <Text textStyle="l">{answerData.answer.text}</Text>
          </Flex>
        ))}
      </Flex>
    </ModuleWrapper>
  );
};

export const iconQuestionFragment = `
  fragment iconQuestionFragment on CourseQuestionRecord {
    __typename
    answers {
      answer {
        backgroundColor {
          backgroundColor
        }
        icon {
          __typename
          ... on IconColorRecord {
            color
          }
          ... on IconRecord {
            name
            color
          }
        }
        summaryText
        text
      }
      code
      courses {
        code
      }
    }
    code
    question
  }
`;
